import { Box, Divider, Grid, LinearProgress, Paper, Typography } from '@mui/material';
import { InvestmentFinancialMetricsDTO } from '@inversor-hub/types';
import { formatCurrency } from '@/utils/misc';
import { cardSurface } from '../shared/surfaces';

interface VacancyPanelProps {
  metrics: InvestmentFinancialMetricsDTO;
  scaleAmount: (cents: number) => number;
}

export const VacancyPanel = ({ metrics, scaleAmount }: VacancyPanelProps) => {
  const { availableDays, vacantDays, estimatedLostRent, coverage } = metrics.vacancy;

  if (coverage === 'NONE' || availableDays === 0) return null;

  const occupiedDays = availableDays - vacantDays;
  const occupancyPct = (occupiedDays / availableDays) * 100;

  return (
    <Paper elevation={0} sx={{ p: 3, height: '100%', ...cardSurface }}>
      <Typography variant="h6" fontWeight={600}>
        Ocupación
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Días alquilados frente a días disponibles, deducidos del histórico de alquileres.
      </Typography>
      <Divider sx={{ mb: 2.5 }} />

      <Box sx={{ mb: 2.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
          <Typography variant="body2" fontWeight={600}>
            Tiempo alquilado
          </Typography>
          <Typography variant="h6" fontWeight={700} color="primary.main">
            {occupancyPct.toFixed(1)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(Math.max(occupancyPct, 0), 100)}
          color={occupancyPct >= 90 ? 'success' : 'warning'}
          sx={{ height: 8, borderRadius: 4 }}
          aria-label={`Ocupación del ${occupancyPct.toFixed(1)} por ciento`}
        />
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={6}>
          <Typography variant="overline" color="text.secondary" display="block" lineHeight={1.4}>
            Días vacíos
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {vacantDays} de {availableDays}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="overline" color="text.secondary" display="block" lineHeight={1.4}>
            Renta no ingresada
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {formatCurrency(scaleAmount(estimatedLostRent))}
          </Typography>
        </Grid>
      </Grid>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
        Es una estimación sobre la última renta conocida de cada unidad y un coste de oportunidad:
        no está descontada del beneficio del alquiler ni del cashflow.
      </Typography>
    </Paper>
  );
};
